/**
 * 📡 «کانال‌ها» — جایی که بازار اعلام می‌کند، نه گفتگو.
 *
 * دستیار در کانال هیچ‌وقت پیام نمی‌دهد؛ فقط عضو می‌شود و پست‌ها را می‌خواند تا
 * از خبر محصولات و برندها، لیست قیمت و آگهی رقیب، تحلیل جداگانهٔ کانال‌ها ساخته شود.
 * این تب سه کار دارد: عضو کردن یک حساب در کانال، روشن/خاموش کردن کانال‌ها و
 * دسته‌بندی‌شان، و دیدن پست‌های خوانده‌شده برای اطمینان از اینکه کار می‌کند.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, LinearProgress, MenuItem, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import RefreshIcon from '@mui/icons-material/Refresh';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import AddLinkRoundedIcon from '@mui/icons-material/AddLinkRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExitToAppRoundedIcon from '@mui/icons-material/ExitToAppRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { ResponsiveModal } from '../common';
import userbotService, {
  ChannelCategory, UserbotAccount, UserbotChannel, UserbotChannelPost,
} from '../../services/userbotService';
import {
  Cell, EmptyHint, IconAction, Insights, Kpis, Loading, Pill, Row, Rows, SectionCard, Tag, UB,
  errMsg, fa, faDate, hueOf, initial, relTime,
} from './kit';
import type { Tone } from './kit';

const CATEGORIES: Array<{ key: ChannelCategory; label: string; hint: string; color: string }> = [
  { key: 'news', label: 'خبری', hint: 'خبر برندها و محصولات تازه — برای محتوا و زمان‌بندی', color: UB.info },
  { key: 'competitor', label: 'رقیب', hint: 'فروشگاه رقیب — قیمت و کمپینش را می‌خوانیم', color: UB.bad },
  { key: 'market', label: 'بازار / قیمت', hint: 'لیست قیمت و نرخ روز', color: UB.ok },
  { key: 'community', label: 'انجمن', hint: 'گفتگوی مصرف‌کننده‌ها، نه فروش', color: UB.accent },
  { key: 'other', label: 'سایر', hint: 'هنوز دسته‌بندی نشده', color: UB.muted },
];

const catMeta = (k?: string) => CATEGORIES.find((c) => c.key === k) || CATEGORIES[4];

const toman = (v?: number | null): string => {
  if (!v || !Number.isFinite(v)) return '—';
  if (v >= 1e6) return `${fa(Number((v / 1e6).toFixed(1)))} میلیون`;
  if (v >= 1e3) return `${fa(Math.round(v / 1e3))} هزار`;
  return fa(v);
};

// ────────────────────────── پیش‌نمایش پست‌های یک کانال ──────────────────────────

const PostsModal: React.FC<{ ch: UserbotChannel; onClose: () => void }> = ({ ch, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<UserbotChannelPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setRows(await userbotService.channelPosts(ch._id));
      } catch (e) {
        enqueueSnackbar(errMsg(e), { variant: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [ch._id, enqueueSnackbar]);

  return (
    <ResponsiveModal
      open
      onClose={onClose}
      maxWidth="md"
      icon={<VisibilityRoundedIcon />}
      title={ch.title || ch.chatId}
      subtitle={`${fa(ch.stats?.posts ?? 0)} پست خوانده‌شده · آخرین: ${relTime(ch.lastPostAt)}`}
      actions={<Button size="small" onClick={onClose}>بستن</Button>}
    >
      {loading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <EmptyHint
          icon={<CampaignRoundedIcon />}
          title="هنوز پستی خوانده نشده"
          hint="کانال باید روشن باشد و یک حساب آنلاین عضو آن. خواندن هر سه دقیقه یک بار انجام می‌شود."
        />
      ) : (
        <div className="fac-lines">
          {rows.map((p) => (
            <div key={p._id} className="fac-line" style={{ gridTemplateColumns: '1fr' }}>
              <div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', marginBottom: 5 }}>
                  {p.isNews && <Pill label="خبر" color={UB.info} />}
                  {p.isPromo && <Pill label="تخفیف" color={UB.warn} />}
                  {p.isSale && <Pill label="آگهی فروش" color={UB.bad} />}
                  {p.object && <Tag label={p.object} />}
                  {p.brand && <Tag label={p.brand} />}
                  {p.concern && <Tag label={p.concern} />}
                  {!!p.prices?.length && (
                    <Tag
                      color="success"
                      label={p.minPrice === p.maxPrice ? `${toman(p.minPrice)} تومان` : `${toman(p.minPrice)} تا ${toman(p.maxPrice)} تومان`}
                      title={p.prices.map((x) => toman(x)).join(' · ')}
                    />
                  )}
                  <span style={{ flex: 1 }} />
                  {p.views != null && <small className="fac-muted">{fa(p.views)} بازدید</small>}
                  <small className="fac-muted">{relTime(p.at)}</small>
                </div>
                <div className="ubc-bubble-text">{p.text}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </ResponsiveModal>
  );
};

// ────────────────────────── عضو کردن حساب در کانال ──────────────────────────

const JoinModal: React.FC<{ accounts: UserbotAccount[]; onClose: () => void; onDone: () => void }> = ({
  accounts,
  onClose,
  onDone,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const online = accounts.filter((a) => a.online && a.active);
  const [accountId, setAccountId] = useState(online[0]?._id || '');
  const [links, setLinks] = useState('');
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState<Array<{ link: string; ok: boolean; msg: string }>>([]);

  const list = useMemo(
    () => links.split(/[\s,،]+/).map((s) => s.trim()).filter(Boolean),
    [links]
  );

  const run = async () => {
    if (!accountId || !list.length) return;
    setBusy(true);
    setLog([]);
    for (const link of list) {
      try {
        const msg = await userbotService.joinChannel(accountId, link);
        setLog((l) => [...l, { link, ok: true, msg }]);
      } catch (e) {
        setLog((l) => [...l, { link, ok: false, msg: errMsg(e) }]);
      }
    }
    setBusy(false);
    try {
      await userbotService.refreshDialogs(accountId);
    } catch {
      /* فهرست بعداً خودش به‌روز می‌شود */
    }
    onDone();
    enqueueSnackbar('عضویت‌ها انجام شد؛ فهرست کانال‌ها به‌روز شد', { variant: 'success' });
  };

  return (
    <ResponsiveModal
      open
      onClose={onClose}
      maxWidth="sm"
      disableClose={busy}
      icon={<AddLinkRoundedIcon />}
      title="عضویت در کانال"
      subtitle="لینک یا یوزرنیم کانال‌ها را بدهید؛ هر خط یکی."
      actions={
        <>
          <Button size="small" color="inherit" disabled={busy} onClick={onClose}>بستن</Button>
          <Button
            size="small"
            variant="contained"
            disabled={busy || !accountId || !list.length}
            startIcon={busy ? <CircularProgress size={14} /> : <AddLinkRoundedIcon />}
            onClick={run}
          >
            {list.length > 1 ? `عضویت در ${fa(list.length)} کانال` : 'عضو شو'}
          </Button>
        </>
      }
    >
      {online.length === 0 ? (
        <Alert severity="warning">هیچ حساب آنلاینی نیست. اول در تب حساب‌ها وارد شوید.</Alert>
      ) : (
        <Stack spacing={1.5}>
          <TextField
            select
            size="small"
            label="با کدام حساب"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            fullWidth
          >
            {online.map((a) => (
              <MenuItem key={a._id} value={a._id}>
                {a.label}
                {a.username ? ` (@${a.username})` : ''}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="لینک یا یوزرنیم کانال"
            placeholder={'@casmashop_news\nhttps://t.me/example'}
            value={links}
            onChange={(e) => setLinks(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            sx={{ '& textarea': { direction: 'ltr', textAlign: 'left' } }}
          />
          <Alert severity="info" sx={{ '& .MuiAlert-message': { lineHeight: 1.9 } }}>
            عضویت انبوه و پشت‌سرهم با یک حساب تازه، تلگرام را حساس می‌کند. چند تا چند تا و با فاصله اضافه کنید.
          </Alert>
          {log.map((l, i) => (
            <Alert key={i} severity={l.ok ? 'success' : 'error'} sx={{ py: 0.25 }}>
              <span style={{ direction: 'ltr', display: 'inline-block' }}>{l.link}</span> — {l.msg}
            </Alert>
          ))}
        </Stack>
      )}
    </ResponsiveModal>
  );
};

// ────────────────────────── خروج از کانال‌ها (یکجا یا تکی) ──────────────────────────

const LeaveModal: React.FC<{ rows: UserbotChannel[]; onClose: () => void; onDone: () => void }> = ({
  rows,
  onClose,
  onDone,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [picked, setPicked] = useState<string[]>([]);
  const [keep, setKeep] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);
  const [log, setLog] = useState<Array<{ id: string; title: string; ok: boolean; message?: string }>>([]);

  const all = picked.length === rows.length;
  const toggle = (id: string) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  /**
   * هر خروج یک رفت‌وبرگشت تا تلگرام است، پس سرور هر بار چند کانال را انجام
   * می‌دهد و بقیه را `remaining` می‌گوید. این‌جا تا تمام‌شدن ادامه می‌دهیم و
   * پیشرفت را نشان می‌دهیم — به این شکل درخواست هیچ‌وقت تایم‌اوت نمی‌شود.
   */
  const run = async () => {
    if (!picked.length) return;
    setBusy(true);
    setLog([]);
    setDone(0);

    // همیشه شناسه‌های صریح می‌فرستیم (حتی وقتی «همه» انتخاب شده) تا بدانیم کدام
    // انجام شده؛ وگرنه در حالت «در فهرست بماند» سرور هر بار همان‌ها را برمی‌دارد.
    let pending = [...picked];
    let finished = 0;
    let failed = 0;

    try {
      // سقف امنیتی تا اگر سرور چیزی را رد کرد در حلقهٔ بی‌پایان نیفتیم
      for (let round = 0; round < 500 && pending.length; round++) {
        const r = await userbotService.leaveChannels(pending, keep);
        setLog((l) => [...l, ...r.result.results]);
        finished += r.result.done;
        failed += r.result.failed;
        setDone(finished);

        // هرچه سرور جواب داد (موفق یا ناموفق) از صف بیرون می‌رود تا گیر نکنیم
        const seen = r.result.results.map((x) => x.id);
        const next = pending.filter((id) => !seen.includes(id));
        if (next.length === pending.length) break; // سرور هیچ‌کدام را برنداشت
        pending = next;
      }

      enqueueSnackbar(
        failed ? `${fa(finished - failed)} کانال خارج شد؛ ${fa(failed)} مورد خطا داد` : `از ${fa(finished)} کانال خارج شد`,
        { variant: failed ? 'warning' : 'success' }
      );
      onDone();
      if (!failed) onClose();
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
      onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onClose={onClose}
      maxWidth="sm"
      disableClose={busy}
      icon={<ExitToAppRoundedIcon />}
      title="خروج از کانال‌ها"
      subtitle="حساب‌ها از کانال‌های انتخاب‌شده بیرون می‌آیند. می‌توانید همه را یکجا بزنید یا تکی انتخاب کنید."
      actions={
        <>
          <Button size="small" color="inherit" disabled={busy} onClick={onClose}>بستن</Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            disabled={busy || !picked.length}
            startIcon={busy ? <CircularProgress size={14} /> : <ExitToAppRoundedIcon />}
            onClick={run}
          >
            {busy
              ? `در حال خروج… ${fa(done)}/${fa(picked.length)}`
              : all
                ? 'خروج از همهٔ کانال‌ها'
                : `خروج از ${fa(picked.length)} کانال`}
          </Button>
        </>
      }
    >
      <Stack spacing={1.25}>
        {busy && (
          <Box>
            <LinearProgress
              variant="determinate"
              value={picked.length ? (done / picked.length) * 100 : 0}
              sx={{ height: 6, borderRadius: 3 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              چند تا چند تا انجام می‌شود تا تلگرام حساس نشود. پنجره را نبندید.
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" disabled={busy} onClick={() => setPicked(rows.map((r) => r._id))}>
            انتخاب همه ({fa(rows.length)})
          </Button>
          <Button size="small" color="inherit" disabled={busy || !picked.length} onClick={() => setPicked([])}>
            هیچ‌کدام
          </Button>
        </Box>

        <div className="fac-card ubc-scroll" style={{ margin: 0, maxHeight: 280, minHeight: 0 }}>
          {rows.map((c) => (
            <div key={c._id} className={`ubc-pick ${picked.includes(c._id) ? 'on' : ''}`} onClick={() => !busy && toggle(c._id)}>
              <Checkbox size="small" checked={picked.includes(c._id)} disabled={busy} />
              <div className="ubc-pick-main">
                <b>{c.title || c.chatId}</b>
                <small>{(c.accountIds || []).map((a) => a.label).join('، ') || 'بدون حساب عضو'}</small>
              </div>
              {c.enabled && <Tag color="success" label="روشن" />}
            </div>
          ))}
        </div>

        <Box
          onClick={() => !busy && setKeep((k) => !k)}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: busy ? 'default' : 'pointer' }}
        >
          <Checkbox size="small" checked={keep} disabled={busy} />
          <Typography variant="body2">در فهرست بماند (خاموش و بدون عضو) تا بعداً دوباره اضافه شود</Typography>
        </Box>

        <Alert severity={keep ? 'info' : 'warning'} sx={{ '& .MuiAlert-message': { lineHeight: 1.9 } }}>
          {keep
            ? 'کانال‌ها در فهرست می‌مانند ولی خاموش می‌شوند و دیگر پستی از آن‌ها خوانده نمی‌شود.'
            : 'کانال‌ها از فهرست هم حذف می‌شوند. اگر خروج ناموفق بود، کانال در فهرست می‌ماند تا دوباره امتحان کنید. پست‌های خوانده‌شده و گزارش‌ها دست‌نخورده می‌مانند.'}
        </Alert>

        {log.map((l, i) => (
          <Alert key={i} severity={l.ok ? 'success' : 'error'} sx={{ py: 0.25 }}>
            {l.title} — {l.ok ? 'خارج شد' : l.message || 'ناموفق'}
          </Alert>
        ))}
      </Stack>
    </ResponsiveModal>
  );
};

// ────────────────────────── تب ──────────────────────────

const ChannelsTab: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<UserbotChannel[]>([]);
  const [accounts, setAccounts] = useState<UserbotAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [join, setJoin] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [posts, setPosts] = useState<UserbotChannel | null>(null);
  const [del, setDel] = useState<UserbotChannel | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [noteOf, setNoteOf] = useState<UserbotChannel | null>(null);
  const [noteText, setNoteText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, a] = await Promise.all([userbotService.channels(), userbotService.accounts()]);
      setRows(c);
      setAccounts(a);
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);
  useEffect(() => { load(); }, [load]);

  const patch = async (ch: UserbotChannel, body: Parameters<typeof userbotService.updateChannel>[1]) => {
    try {
      const next = await userbotService.updateChannel(ch._id, body);
      setRows((r) => r.map((x) => (x._id === ch._id ? { ...x, ...next } : x)));
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
      load();
    }
  };

  const remove = async (leave: boolean) => {
    if (!del) return;
    setDeleting(true);
    try {
      enqueueSnackbar(await userbotService.deleteChannel(del._id, leave), { variant: 'success' });
      setDel(null);
      load();
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const shown = useMemo(
    () => rows.filter((c) => !filter || (c.title || '').includes(filter) || (c.username || '').includes(filter)),
    [rows, filter]
  );
  const enabledCount = rows.filter((c) => c.enabled).length;
  const totalPosts = rows.reduce((s, c) => s + (c.stats?.posts || 0), 0);

  const insights: Array<{ tone: Tone; text: React.ReactNode }> = [];
  if (!loading && rows.length > 0) {
    const noAcc = rows.filter((c) => c.enabled && !(c.accountIds || []).some((a) => a.online)).length;
    const stale = rows.filter((c) => c.enabled && c.lastPostAt && Date.now() - new Date(c.lastPostAt).getTime() > 3 * 86400000).length;
    if (!enabledCount) insights.push({ tone: 'warn', text: <>هیچ کانالی روشن نیست؛ تا روشن نکنید هیچ پستی خوانده نمی‌شود و «تحلیل بازار ← کانال‌ها» خالی می‌ماند.</> });
    else insights.push({ tone: 'info', text: <><b>{fa(enabledCount)}</b> کانال روشن است و تا حالا <b>{fa(totalPosts)}</b> پست خوانده شده. این‌ها فقط خوانده می‌شوند؛ دستیار در کانال هیچ‌وقت پیام نمی‌دهد.</> });
    if (noAcc) insights.push({ tone: 'warn', text: <>{fa(noAcc)} کانال روشن است ولی هیچ حساب آنلاینی عضوش نیست؛ عملاً پستی خوانده نمی‌شود.</> });
    if (stale) insights.push({ tone: 'warn', text: <>{fa(stale)} کانال روشن بیش از سه روز است پست تازه‌ای نداشته؛ شاید کانال مرده یا حساب از آن خارج شده است.</> });
  }

  return (
    <>
      <Insights items={insights} />
      {rows.length > 0 && (
        <Kpis items={[
          { label: 'کانال‌های روشن', raw: <>{fa(enabledCount)} <span className="fac-muted">/ {fa(rows.length)}</span></>, value: null, color: enabledCount ? 'green' : 'gray', help: 'فقط از کانال‌های روشن پست خوانده می‌شود' },
          { label: 'پست خوانده‌شده', value: totalPosts, count: true, color: 'blue', help: 'مجموع همهٔ کانال‌ها؛ پست‌ها تا ۶۰ روز نگه داشته می‌شوند' },
          { label: 'پست قیمت‌دار', value: rows.reduce((s2, c) => s2 + (c.stats?.priceMentions || 0), 0), count: true, color: 'purple', help: 'پست‌هایی که عدد قیمت داشتند — مبنای «قیمت بازار»' },
          { label: 'تخفیف و کمپین', value: rows.reduce((s2, c) => s2 + (c.stats?.promos || 0), 0), count: true, color: 'orange', help: 'حرکت‌های تبلیغاتی رقبا' },
        ]} />
      )}
      <SectionCard
        icon={<CampaignRoundedIcon />}
        title="کانال‌ها"
        hint="دستیار در کانال هیچ‌وقت پیام نمی‌دهد؛ فقط می‌خواند. پست‌های کانال‌های روشن هر سه دقیقه خوانده و در «تحلیل بازار ← کانال‌ها» تحلیل می‌شوند"
        actions={
          <>
            <TextField size="small" placeholder="جست‌وجوی کانال…" value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ minWidth: 180 }} />
            <Button size="small" variant="contained" startIcon={<AddLinkRoundedIcon />} onClick={() => setJoin(true)}>افزودن کانال</Button>
            {rows.length > 0 && (
              <Button size="small" color="error" variant="outlined" startIcon={<ExitToAppRoundedIcon />} onClick={() => setLeaveOpen(true)}>خروج از کانال‌ها</Button>
            )}
            <IconAction title="به‌روزرسانی" onClick={load}><RefreshIcon /></IconAction>
          </>
        }
      >
        {loading && shown.length === 0 ? (
          <Loading />
        ) : shown.length === 0 ? (
          <EmptyHint
            icon={<CampaignRoundedIcon />}
            title={rows.length ? 'با این جست‌وجو کانالی نیست' : 'هنوز کانالی نیست'}
            hint={rows.length ? 'عبارت جست‌وجو را عوض کنید.' : 'با «افزودن کانال» یک حساب را عضو کانال کنید، یا در تب حساب‌ها روی «گروه‌ها و کانال‌ها» بزنید تا عضویت‌های فعلی خوانده شود.'}
            action={rows.length ? undefined : <Button size="small" variant="contained" startIcon={<AddLinkRoundedIcon />} onClick={() => setJoin(true)}>افزودن کانال</Button>}
          />
        ) : (
          <Rows cols="52px minmax(180px, 1.6fr) 150px 150px minmax(190px, 1.1fr) minmax(150px, 1fr) 76px" minWidth={1040}>
            <Row head>
              <span>روشن</span><span>کانال</span><span>دسته</span><span>حساب‌های عضو</span><span>آمار</span><span>یادداشت</span><span />
            </Row>
            {shown.map((c) => {
              const meta = catMeta(c.category);
              const online = (c.accountIds || []).filter((a) => a.online).length;
              return (
                <Row key={c._id} muted={!c.enabled}>
                  <span>
                    <Tooltip title={c.enabled ? 'روشن — پست‌ها خوانده می‌شوند' : 'خاموش — هیچ پستی خوانده نمی‌شود'} arrow>
                      <Switch size="small" checked={c.enabled} onChange={(e) => patch(c, { enabled: e.target.checked })} />
                    </Tooltip>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span className="ubc-avatar sm" style={{ background: hueOf(c.chatId) }}>{initial(c.title || c.chatId)}</span>
                    <Cell
                      top={c.title || c.chatId}
                      sub={`${c.username ? `@${c.username}` : c.chatId}${c.membersCount != null ? ` · ${fa(c.membersCount)} عضو` : ''}`}
                    />
                  </span>
                  <span>
                    <TextField
                      select size="small" value={c.category || 'other'}
                      onChange={(e) => patch(c, { category: e.target.value as ChannelCategory })}
                      sx={{ minWidth: 130 }} title={meta.hint}
                    >
                      {CATEGORIES.map((x) => <MenuItem key={x.key} value={x.key}>{x.label}</MenuItem>)}
                    </TextField>
                  </span>
                  <span>
                    <Tag
                      label={c.accountIds?.length ? `${fa(online)}/${fa(c.accountIds.length)} آنلاین` : 'بدون حساب'}
                      color={c.accountIds?.length ? (online ? 'success' : 'warning') : 'error'}
                      title={(c.accountIds || []).map((a) => a.label).join('، ') || 'هیچ حسابی عضو این کانال نیست'}
                    />
                  </span>
                  <span style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span className="ubc-mini"><b>{fa(c.stats?.posts ?? 0)}</b><i>پست</i></span>
                    <span className="ubc-mini"><b>{fa(c.stats?.priceMentions ?? 0)}</b><i>قیمت‌دار</i></span>
                    <span className="ubc-mini"><b>{fa(c.stats?.promos ?? 0)}</b><i>تخفیف</i></span>
                    <small className="fac-muted" style={{ flexBasis: '100%' }}>آخرین پست {relTime(c.lastPostAt)}</small>
                  </span>
                  <span style={{ minWidth: 0 }}>
                    {c.note
                      ? <small style={{ display: 'block', lineHeight: 1.8, color: 'var(--color-text-secondary)' }}>{c.note}</small>
                      : <small className="fac-muted">بدون یادداشت</small>}
                    <Button size="small" sx={{ p: 0, minWidth: 0, fontSize: 11 }} onClick={() => { setNoteOf(c); setNoteText(c.note || ''); }}>
                      {c.note ? 'ویرایش' : 'افزودن یادداشت'}
                    </Button>
                  </span>
                  <span style={{ display: 'flex', gap: 2 }}>
                    <IconAction title="پست‌های خوانده‌شده" onClick={() => setPosts(c)}><VisibilityRoundedIcon fontSize="small" /></IconAction>
                    <IconAction title="خروج / حذف این کانال" color="error" onClick={() => setDel(c)}><DeleteOutlineIcon fontSize="small" /></IconAction>
                  </span>
                </Row>
              );
            })}
          </Rows>
        )}
      </SectionCard>

      {join && <JoinModal accounts={accounts} onClose={() => setJoin(false)} onDone={load} />}
      {leaveOpen && <LeaveModal rows={rows} onClose={() => setLeaveOpen(false)} onDone={load} />}
      {posts && <PostsModal ch={posts} onClose={() => setPosts(null)} />}

      {noteOf && (
        <ResponsiveModal
          open
          onClose={() => setNoteOf(null)}
          maxWidth="sm"
          icon={<CampaignRoundedIcon />}
          title={`یادداشت — ${noteOf.title || noteOf.chatId}`}
          subtitle="این متن به مدل داده می‌شود تا بداند این کانال چیست و پست‌هایش را چطور بخواند."
          actions={
            <>
              <Button size="small" color="inherit" onClick={() => setNoteOf(null)}>انصراف</Button>
              <Button
                size="small"
                variant="contained"
                onClick={async () => {
                  await patch(noteOf, { note: noteText });
                  setNoteOf(null);
                }}
              >
                ذخیره
              </Button>
            </>
          }
        >
          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="مثلاً: فروشگاه رقیب، بیشتر ضدآفتاب و سرم پوست می‌فروشد و جمعه‌ها تخفیف می‌گذارد."
          />
        </ResponsiveModal>
      )}

      {del && (
        <ResponsiveModal
          open
          onClose={() => setDel(null)}
          maxWidth="xs"
          disableClose={deleting}
          icon={<DeleteOutlineIcon />}
          title="حذف کانال"
          subtitle={del.title || del.chatId}
          actions={
            <>
              <Button size="small" color="inherit" disabled={deleting} onClick={() => setDel(null)}>انصراف</Button>
              <Button size="small" color="warning" disabled={deleting} onClick={() => remove(false)}>
                فقط حذف از فهرست
              </Button>
              <Button size="small" color="error" variant="contained" disabled={deleting} onClick={() => remove(true)}>
                خروج حساب‌ها و حذف
              </Button>
            </>
          }
        >
          <Typography variant="body2" sx={{ lineHeight: 2 }}>
            «فقط حذف از فهرست» کانال را از پنل برمی‌دارد ولی حساب‌ها همچنان عضو می‌مانند و می‌توانید بعداً دوباره اضافه‌اش کنید.
            «خروج حساب‌ها و حذف» حساب‌ها را هم از کانال بیرون می‌آورد.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            پست‌های خوانده‌شده تا شصت روز می‌مانند و گزارش‌های ساخته‌شده پاک نمی‌شوند.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            آخرین خواندن: {faDate(del.lastFetchAt)}
          </Typography>
        </ResponsiveModal>
      )}
    </>
  );
};

export default ChannelsTab;
